import React, { memo } from "react";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Company } from "@mapmycustomers/shared/types/entity";

import AddOrCreateDropdown from "@app/component/preview/components/AddOrCreateDropdown";
import Section from "@app/component/preview/components/Section";

import styles from "../../Relationships.module.scss";
import EditRelatedCompanies from "../EditRelatedCompanies";

const messages = defineMessages({
  addCompany: {
    id: "entityPreview.relationships.parentCompanies.noData.restEntityTypes",
    defaultMessage: "Add a company",
    description: "Text to show when there's no company yet in Company Record pane",
  },
  companies: {
    id: "entityPreview.relationships.parentCompanies.companies",
    defaultMessage: "Companies ({count})",
    description: "Parent company section title for rest entity types of the preview record pane",
  },
});

interface Props {
  addCompanyParentCompaniesLoading?: boolean;
  companies: Company[];
  onAddExisting?: () => void;
  onAddNew: () => void;
  onChangePrimaryCompany?: () => void;
  onFetchMoreCompanies?: () => void;
  onRemove?: (company: Company) => void;
  onUnsetPrimaryCompany?: () => void;
  parentCompaniesFiltered: Company[];
  primaryCompanyId?: Company["id"];
  totalCompanies: number;
}

const ParentCompanies: React.FC<Props> = ({
  addCompanyParentCompaniesLoading = false,
  companies,
  onAddExisting,
  onAddNew,
  onChangePrimaryCompany,
  onFetchMoreCompanies,
  onRemove,
  onUnsetPrimaryCompany,
  parentCompaniesFiltered,
  primaryCompanyId,
  totalCompanies,
}) => {
  const intl = useIntl();

  return (
    <Section
      action={
        <AddOrCreateDropdown
          disabled={totalCompanies >= 100}
          loading={addCompanyParentCompaniesLoading}
          onAddExisting={onAddExisting}
          onAddNew={onAddNew}
          onChangePrimary={onChangePrimaryCompany}
          onUnsetPrimary={onUnsetPrimaryCompany}
          showPrimary
        />
      }
      title={intl.formatMessage(messages.companies, { count: totalCompanies })}
      titleClassName={styles.sectionTitle}
    >
      <div
        className={cn(styles.container, {
          [styles.noParent]: parentCompaniesFiltered && parentCompaniesFiltered.length === 0,
        })}
      >
        <EditRelatedCompanies
          companies={companies}
          noDataTitle={intl.formatMessage(messages.addCompany)}
          onFetchMoreCompanies={onFetchMoreCompanies}
          onRemove={onRemove}
          parentCompaniesFiltered={parentCompaniesFiltered}
          primaryCompanyId={primaryCompanyId}
          totalCompanies={totalCompanies}
        />
        {/* a dummy div to make grid repeat(auto-fit) work correctly */}
        <div />
      </div>
    </Section>
  );
};

export default memo(ParentCompanies);
